

import { useState, useRef, useEffect, useCallback } from 'react'
import { FiVolume2, FiVolumeX, FiMaximize, FiMinimize, FiVolume1, FiRepeat, FiChevronLeft, FiChevronRight, FiInfo, FiCopy, FiExternalLink } from 'react-icons/fi'
import { IoPlay, IoPause } from 'react-icons/io5'
import { LuLoaderCircle } from "react-icons/lu";
import { Slider } from '@/components/ui/shadcn/slider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select'

const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
const DEFAULT_FPS = 30
const VOLUME_STORAGE_KEY = 'videoPlayerVolume'
const MUTED_STORAGE_KEY = 'videoPlayerMuted'

// Helper to get saved volume from localStorage
const getSavedVolume = () => {
    if (typeof window === 'undefined') return 1
    try {
        const saved = localStorage.getItem(VOLUME_STORAGE_KEY)
        if (saved !== null) {
            const vol = parseFloat(saved)
            if (!isNaN(vol) && vol >= 0 && vol <= 1) return vol
        }
    } catch (e) {
        // localStorage might be unavailable
    }
    return 1
}

// Helper to get saved muted state from localStorage
const getSavedMuted = () => {
    if (typeof window === 'undefined') return false
    try {
        const saved = localStorage.getItem(MUTED_STORAGE_KEY)
        if (saved !== null) {
            return saved === 'true'
        }
    } catch (e) {
        // localStorage might be unavailable
    }
    return false
}

const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function VideoPlayer({
    src,
    asset,
    poster,
    autoPlay = false,
    muted = false,
    loop = false,
    startTime,
    allowFullscreen = true,
    showPlayOverlay = true,
    className = '',
    onPlay,
    onPause,
    onEnded,
    onTimeUpdate,
    onLoadedMetadata,
    ...props
}) {
    // Asset URL takes priority over raw src
    const videoSrc = asset?.url || src

    const videoRef = useRef(null)
    const containerRef = useRef(null)
    const progressRef = useRef(null)
    const hideControlsTimeout = useRef(null)
    const animationFrameRef = useRef(null)
    const isFirstMount = useRef(true)
    const hasAppliedStartTime = useRef(false)

    const [isLoading, setIsLoading] = useState(true)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(() => muted || getSavedMuted())
    const [isLooping, setIsLooping] = useState(loop)
    const [volume, setVolume] = useState(() => (muted || getSavedMuted()) ? 0 : getSavedVolume())
    const [previousVolume, setPreviousVolume] = useState(() => getSavedVolume())
    const [currentTime, setCurrentTime] = useState(0)
    const [displayTime, setDisplayTime] = useState(0) // Separate state for smooth visual updates
    const [duration, setDuration] = useState(0)
    const [fps, setFps] = useState(null) // Detected frame rate (null until detected)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)
    const [showControls, setShowControls] = useState(true)
    const [isDragging, setIsDragging] = useState(false)
    const [isHovering, setIsHovering] = useState(false)
    const [buffered, setBuffered] = useState(0)
    const [showInfo, setShowInfo] = useState(false)
    const [videoResolution, setVideoResolution] = useState({ width: 0, height: 0 })

    // Calculate frame time based on detected fps (fallback to DEFAULT_FPS if not detected)
    const frameTime = 1 / (fps || DEFAULT_FPS)

    // Play/Pause toggle
    const togglePlay = useCallback(() => {
        if (!videoRef.current) return
        if (isPlaying) {
            videoRef.current.pause()
        } else {
            videoRef.current.play()
        }
    }, [isPlaying])

    // Mute toggle
    const toggleMute = useCallback(() => {
        if (!videoRef.current) return
        if (isMuted) {
            videoRef.current.muted = false
            videoRef.current.volume = previousVolume
            setVolume(previousVolume)
            setIsMuted(false)
        } else {
            setPreviousVolume(volume)
            videoRef.current.muted = true
            videoRef.current.volume = 0
            setVolume(0)
            setIsMuted(true)
        }
    }, [isMuted, volume, previousVolume])

    // Loop toggle
    const toggleLoop = useCallback(() => {
        if (!videoRef.current) return
        const newLoopState = !isLooping
        videoRef.current.loop = newLoopState
        setIsLooping(newLoopState)
    }, [isLooping])

    // Frame skip
    const skipFrame = useCallback((direction) => {
        if (!videoRef.current) return
        const newTime = direction > 0
            ? Math.min(duration, videoRef.current.currentTime + frameTime)
            : Math.max(0, videoRef.current.currentTime - frameTime)
        videoRef.current.currentTime = newTime
        setCurrentTime(newTime)
        setDisplayTime(newTime)
    }, [duration, frameTime])

    // Volume change
    const handleVolumeChange = useCallback((value) => {
        if (!videoRef.current) return
        const newVolume = value[0]
        videoRef.current.volume = newVolume
        videoRef.current.muted = newVolume === 0
        setVolume(newVolume)
        setIsMuted(newVolume === 0)
        if (newVolume > 0) {
            setPreviousVolume(newVolume)
        }
    }, [])

    // Fullscreen toggle
    const toggleFullscreen = useCallback(async () => {
        if (!containerRef.current || !allowFullscreen) return
        
        try {
            if (!document.fullscreenElement) {
                await containerRef.current.requestFullscreen()
            } else {
                await document.exitFullscreen()
            }
        } catch (err) {
            console.error('Fullscreen error:', err)
        }
    }, [allowFullscreen])

    // Playback speed change
    const handleSpeedChange = useCallback((value) => {
        if (!videoRef.current) return
        const speed = parseFloat(value)
        videoRef.current.playbackRate = speed
        setPlaybackSpeed(speed)
    }, [])

    // Calculate time from mouse position
    const getTimeFromMouseEvent = useCallback((e) => {
        if (!progressRef.current || !duration) return 0
        const rect = progressRef.current.getBoundingClientRect()
        const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        return pos * duration
    }, [duration])

    // Progress bar click/drag - immediate visual update
    const handleProgressMouseDown = useCallback((e) => {
        e.preventDefault()
        const newTime = getTimeFromMouseEvent(e)
        setIsDragging(true)
        setDisplayTime(newTime) // Immediate visual feedback
        // Pause video when starting to scrub
        if (videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause()
        }
    }, [getTimeFromMouseEvent])

    const handleProgressMouseMove = useCallback((e) => {
        if (!isDragging) return
        const newTime = getTimeFromMouseEvent(e)
        setDisplayTime(newTime) // Immediate visual feedback during drag
        // Update video while scrubbing
        if (videoRef.current) {
            videoRef.current.currentTime = newTime
        }
    }, [isDragging, getTimeFromMouseEvent])

    const handleProgressMouseUp = useCallback((e) => {
        if (!isDragging) return
        const newTime = getTimeFromMouseEvent(e)
        setIsDragging(false)
        // Now actually seek the video
        if (videoRef.current) {
            videoRef.current.currentTime = newTime
            setCurrentTime(newTime)
            setDisplayTime(newTime)
        }
    }, [isDragging, getTimeFromMouseEvent])

    // Smooth progress update using requestAnimationFrame
    const updateProgress = useCallback(() => {
        if (!videoRef.current || isDragging) return
        
        const time = videoRef.current.currentTime
        setCurrentTime(time)
        setDisplayTime(time)
        
        // Update buffered
        if (videoRef.current.buffered.length > 0) {
            const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1)
            setBuffered((bufferedEnd / videoRef.current.duration) * 100)
        }
        
        onTimeUpdate?.(time)
        
        // Continue the animation loop if playing
        if (!videoRef.current.paused && !videoRef.current.ended) {
            animationFrameRef.current = requestAnimationFrame(updateProgress)
        }
    }, [isDragging, onTimeUpdate])

    // Video event handlers
    const handlePlay = useCallback(() => {
        setIsPlaying(true)
        setIsLoading(false) // Video is actually playing now
        // Start smooth progress updates
        animationFrameRef.current = requestAnimationFrame(updateProgress)
        onPlay?.()
    }, [onPlay, updateProgress])

    const handlePause = useCallback(() => {
        setIsPlaying(false)
        // Stop animation loop
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        onPause?.()
    }, [onPause])

    const handleTimeUpdate = useCallback(() => {
        // Only update buffered here, progress is handled by requestAnimationFrame
        if (!videoRef.current) return
        if (videoRef.current.buffered.length > 0) {
            const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1)
            setBuffered((bufferedEnd / videoRef.current.duration) * 100)
        }
    }, [])

    const handleLoadedMetadata = useCallback(() => {
        if (!videoRef.current) return
        setDuration(videoRef.current.duration)
        setVideoResolution({
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight
        })
        // Apply start time if provided and not already applied
        if (startTime != null && !hasAppliedStartTime.current) {
            const clampedTime = Math.max(0, Math.min(startTime, videoRef.current.duration))
            videoRef.current.currentTime = clampedTime
            setCurrentTime(clampedTime)
            setDisplayTime(clampedTime)
            hasAppliedStartTime.current = true
        }
        onLoadedMetadata?.(videoRef.current.duration)
    }, [onLoadedMetadata, startTime])

    const handleCanPlay = useCallback(() => {
        // For non-autoplay videos, hide loader when video is ready to play
        if (!autoPlay) {
            setIsLoading(false)
        }
    }, [autoPlay])

    // Fallback: also handle loadeddata for cases where canplay doesn't fire
    const handleLoadedData = useCallback(() => {
        if (!autoPlay) {
            setIsLoading(false)
        }
    }, [autoPlay])

    const handleEnded = useCallback(() => {
        setIsPlaying(false)
        // Stop animation loop
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        onEnded?.()
    }, [onEnded])

    // Controls visibility
    const showControlsTemporarily = useCallback(() => {
        setShowControls(true)
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current)
        }
        if (isPlaying && !isHovering) {
            hideControlsTimeout.current = setTimeout(() => {
                setShowControls(false)
            }, 3000)
        }
    }, [isPlaying, isHovering])

    const handleMouseEnter = useCallback(() => {
        setIsHovering(true)
        setShowControls(true)
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current)
        }
    }, [])

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false)
        if (isPlaying) {
            hideControlsTimeout.current = setTimeout(() => {
                setShowControls(false)
            }, 1500)
        }
    }, [isPlaying])

    const handleMouseMove = useCallback(() => {
        showControlsTemporarily()
    }, [showControlsTemporarily])

    // Keyboard controls
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case ' ':
            case 'k':
                e.preventDefault()
                togglePlay()
                break
            case 'f':
                e.preventDefault()
                toggleFullscreen()
                break
            case 'm':
                e.preventDefault()
                toggleMute()
                break
            case 'l':
                e.preventDefault()
                toggleLoop()
                break
            case 'i':
                e.preventDefault()
                setShowInfo(prev => !prev)
                break
            case 'ArrowLeft':
                e.preventDefault()
                skipFrame(-1)
                break
            case 'ArrowRight':
                e.preventDefault()
                skipFrame(1)
                break
            case 'ArrowUp':
                e.preventDefault()
                handleVolumeChange([Math.min(1, volume + 0.1)])
                break
            case 'ArrowDown':
                e.preventDefault()
                handleVolumeChange([Math.max(0, volume - 0.1)])
                break
        }
    }, [togglePlay, toggleFullscreen, toggleMute, toggleLoop, skipFrame, handleVolumeChange, volume])

    // Fullscreen change listener
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }, [])

    // Mouse move/up listeners for dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleProgressMouseMove)
            window.addEventListener('mouseup', handleProgressMouseUp)
        }
        return () => {
            window.removeEventListener('mousemove', handleProgressMouseMove)
            window.removeEventListener('mouseup', handleProgressMouseUp)
        }
    }, [isDragging, handleProgressMouseMove, handleProgressMouseUp])

    // Cleanup timeout and animation frame on unmount
    useEffect(() => {
        return () => {
            if (hideControlsTimeout.current) {
                clearTimeout(hideControlsTimeout.current)
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [])

    // Sync muted prop (skip initial mount to preserve localStorage state)
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false
            // On first mount, sync video element with initial state
            if (videoRef.current) {
                videoRef.current.muted = isMuted
                videoRef.current.volume = isMuted ? 0 : volume
            }
            return
        }
        // After first mount, respond to muted prop changes
        if (videoRef.current) {
            videoRef.current.muted = muted
            setIsMuted(muted)
            if (muted) setVolume(0)
        }
    }, [muted])

    // Set initial volume on video element and persist volume changes
    useEffect(() => {
        if (videoRef.current && !isMuted) {
            videoRef.current.volume = volume
        }
        // Save volume to localStorage (only if not muted to preserve the actual volume preference)
        if (volume > 0) {
            try {
                localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString())
            } catch (e) {
                // localStorage might be unavailable
            }
        }
    }, [volume, isMuted])

    // Persist mute state changes
    useEffect(() => {
        try {
            localStorage.setItem(MUTED_STORAGE_KEY, isMuted.toString())
        } catch (e) {
            // localStorage might be unavailable
        }
    }, [isMuted])

    // Reset state when source changes
    useEffect(() => {
        setIsLoading(true)
        setIsPlaying(false)
        setCurrentTime(0)
        setDisplayTime(0)
        setDuration(0)
        setFps(null)
        setVideoResolution({ width: 0, height: 0 })
        hasAppliedStartTime.current = false
        // Cancel any pending animation frame
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        // Explicitly reload the video element to ensure new source is loaded
        if (videoRef.current) {
            videoRef.current.load()
        }
    }, [videoSrc])

    // Capture video resolution when video is ready (handles case where metadata already loaded)
    useEffect(() => {
        const video = videoRef.current
        if (!video) return

        const updateResolution = () => {
            if (video.videoWidth && video.videoHeight) {
                setVideoResolution({
                    width: video.videoWidth,
                    height: video.videoHeight
                })
            }
        }

        // Check if metadata already loaded
        if (video.readyState >= 1) {
            updateResolution()
        }

        // Also listen for loadedmetadata in case it fires later
        video.addEventListener('loadedmetadata', updateResolution)
        return () => video.removeEventListener('loadedmetadata', updateResolution)
    }, [videoSrc])

    // Detect video FPS using requestVideoFrameCallback
    useEffect(() => {
        const video = videoRef.current
        if (!video || !('requestVideoFrameCallback' in video)) return

        let fpsCallbackId = null
        let detectionComplete = false
        let startMediaTime = null
        let startFrameCount = null

        const detectFps = (now, metadata) => {
            if (detectionComplete) return
            
            // Use metadata.mediaTime and metadata.presentedFrames for accurate FPS calculation
            if (startMediaTime === null) {
                startMediaTime = metadata.mediaTime
                startFrameCount = metadata.presentedFrames
                fpsCallbackId = video.requestVideoFrameCallback(detectFps)
                return
            }

            const mediaTimeDelta = metadata.mediaTime - startMediaTime
            const framesDelta = metadata.presentedFrames - startFrameCount

            // Wait until we have enough data (at least 0.15 seconds of playback)
            if (mediaTimeDelta >= 0.15 && framesDelta >= 3) {
                const detectedFps = framesDelta / mediaTimeDelta
                // Round to common fps values
                const commonFps = [24, 25, 30, 48, 50, 60, 120]
                const closestFps = commonFps.reduce((prev, curr) => 
                    Math.abs(curr - detectedFps) < Math.abs(prev - detectedFps) ? curr : prev
                )
                setFps(closestFps)
                detectionComplete = true
                return
            }

            // Continue detecting
            fpsCallbackId = video.requestVideoFrameCallback(detectFps)
        }

        // Start detection when video plays
        const startDetection = () => {
            startMediaTime = null
            startFrameCount = null
            detectionComplete = false
            fpsCallbackId = video.requestVideoFrameCallback(detectFps)
        }

        video.addEventListener('play', startDetection)

        // If video is already playing, start immediately
        if (!video.paused) {
            startDetection()
        }

        return () => {
            video.removeEventListener('play', startDetection)
            detectionComplete = true
            if (fpsCallbackId && 'cancelVideoFrameCallback' in video) {
                video.cancelVideoFrameCallback(fpsCallbackId)
            }
        }
    }, [videoSrc])

    // Use displayTime for smooth visual progress (updated via RAF or during scrubbing)
    const progress = duration > 0 ? (displayTime / duration) * 100 : 0

    const VolumeIcon = isMuted || volume === 0 
        ? FiVolumeX 
        : volume < 0.5 
            ? FiVolume1 
            : FiVolume2

    return (
        <div
            ref={containerRef}
            className={`relative bg-black overflow-hidden group select-none ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            {/* Video Element */}
            <video
                ref={videoRef}
                src={videoSrc}
                poster={poster}
                autoPlay={autoPlay}
                loop={isLooping}
                playsInline
                className="w-full h-full object-contain"
                onPlay={handlePlay}
                onPause={handlePause}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onCanPlay={handleCanPlay}
                onLoadedData={handleLoadedData}
                onEnded={handleEnded}
                onClick={togglePlay}
                {...props}
            />

            {/* Loading overlay */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <LuLoaderCircle className="w-10 h-10 text-primary animate-spin" />
                </div>
            )}

            {/* Play overlay (center) */}
            {!isLoading && showPlayOverlay && !isPlaying && !isDragging && (
                <div 
                    className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-opacity"
                    onClick={togglePlay}
                >
                    <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center hover:bg-primary transition-colors">
                        <IoPlay className="w-7 h-7 text-primary-foreground ml-1" />
                    </div>
                </div>
            )}

            {/* Info Card */}
            {showInfo && (
                <div className="absolute bottom-20 right-3 bg-black/80 backdrop-blur-sm rounded-lg p-3 text-xs text-white font-mono border border-secondary-foreground/20">
                    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                        <span className="text-secondary-foreground/60">Resolution</span>
                        <span>{videoResolution.width} × {videoResolution.height}</span>
                        
                        <span className="text-secondary-foreground/60">Duration</span>
                        <span>{Math.round(duration * 1000)}ms <span className="text-secondary-foreground/60">({formatTime(duration)})</span></span>
                        
                        <span className="text-secondary-foreground/60">Current Time</span>
                        <span>{Math.round(displayTime * 1000)}ms <span className="text-secondary-foreground/60">({formatTime(displayTime)})</span></span>
                        
                        <span className="text-secondary-foreground/60">FPS</span>
                        <span className={`${fps ? '' : 'text-secondary-foreground/40 italic text-xs'}`}>{fps ?? 'Play to detect FPS'}</span>
                        
                        <span className="text-secondary-foreground/60">Speed</span>
                        <span>{playbackSpeed}x</span>
                        
                        <span className="text-secondary-foreground/60">Source</span>
                        <div className="flex items-center gap-2">
                            <span 
                                className="truncate max-w-[150px] direction-rtl text-left" 
                                dir="rtl"
                                title={videoSrc}
                            >
                                {videoSrc || 'N/A'}
                            </span>
                            {videoSrc && (
                                <>
                                    <button
                                        onClick={() => navigator.clipboard.writeText(videoSrc)}
                                        className="p-1 rounded hover:bg-secondary-foreground/20 text-secondary-foreground/60 hover:text-white transition-colors flex-shrink-0 outline-none cursor-pointer"
                                        aria-label="Copy source URL"
                                    >
                                        <FiCopy className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => window.open(videoSrc, '_blank')}
                                        className="p-1 rounded hover:bg-secondary-foreground/20 text-secondary-foreground/60 hover:text-white transition-colors flex-shrink-0 outline-none cursor-pointer"
                                        aria-label="Open in new tab"
                                    >
                                        <FiExternalLink className="w-3 h-3" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Controls Bar */}
            {!isLoading && (
            <div 
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-8 pb-2 px-3 transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
                {/* Progress Bar - Hover area wrapper */}
                <div 
                    ref={progressRef}
                    className="relative h-[8px] mb-3 cursor-pointer group/progress flex items-center"
                    onMouseDown={handleProgressMouseDown}
                >
                    {/* Visual progress track */}
                    <div className="relative w-full h-[2px] group-hover/progress:h-[8px] bg-secondary-foreground/30 rounded-full transition-all duration-150">
                        {/* Buffered */}
                        <div 
                            className="absolute top-0 left-0 h-full bg-secondary-foreground/40 rounded-full"
                            style={{ width: `${buffered}%` }}
                        />
                        {/* Progress */}
                        <div 
                            className="absolute top-0 left-0 h-full bg-primary rounded-full"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    {/* Thumb */}
                    <div 
                        className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-md transition-opacity ${isDragging ? 'opacity-100' : 'opacity-0 group-hover/progress:opacity-100'}`}
                        style={{ left: `calc(${progress}% - 6px)` }}
                    />
                </div>

                {/* Controls Row */}
                <div className="relative flex items-center justify-between gap-2">
                    {/* Left Controls */}
                    <div className="flex items-center gap-1">
                        {/* Volume */}
                        <div className="flex items-center gap-1 group/volume">
                            <button
                                onClick={toggleMute}
                                className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                                aria-label={isMuted ? 'Unmute' : 'Mute'}
                            >
                                <VolumeIcon className="w-5 h-5" />
                            </button>
                            <div className="w-0 overflow-hidden group-hover/volume:w-20 transition-all duration-200">
                                <Slider
                                    value={[volume]}
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    onValueChange={handleVolumeChange}
                                    className="w-20"
                                />
                            </div>
                        </div>

                        {/* Time */}
                        <div className="text-white text-xs font-mono ml-1">
                            <span>{formatTime(displayTime)}</span>
                            <span className="text-secondary-foreground/60 mx-1">/</span>
                            <span className="text-secondary-foreground/80">{formatTime(duration)}</span>
                        </div>
                    </div>

                    {/* Center Controls - Absolute centered */}
                    <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1">
                        {/* Frame Back */}
                        <button
                            onClick={() => skipFrame(-1)}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label="Previous Frame"
                        >
                            <FiChevronLeft className="w-5 h-5" />
                        </button>

                        {/* Play/Pause */}
                        <button
                            onClick={togglePlay}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <IoPause className="w-5 h-5" />
                            ) : (
                                <IoPlay className="w-5 h-5" />
                            )}
                        </button>

                        {/* Frame Forward */}
                        <button
                            onClick={() => skipFrame(1)}
                            className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                            aria-label="Next Frame"
                        >
                            <FiChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Right Controls */}
                    <div className="flex items-center gap-1">
                        {/* Info */}
                        <button
                            onClick={() => setShowInfo(!showInfo)}
                            className={`p-1.5 rounded hover:bg-secondary-foreground/20 transition-colors outline-none cursor-pointer ${showInfo ? 'text-primary' : 'text-white'}`}
                            aria-label={showInfo ? 'Hide Info' : 'Show Info'}
                        >
                            <FiInfo className="w-4 h-4" />
                        </button>

                        {/* Loop */}
                        <button
                            onClick={toggleLoop}
                            className={`p-1.5 rounded hover:bg-secondary-foreground/20 transition-colors outline-none cursor-pointer ${isLooping ? 'text-primary' : 'text-white'}`}
                            aria-label={isLooping ? 'Disable Loop' : 'Enable Loop'}
                        >
                            <FiRepeat className="w-4 h-4" />
                        </button>

                        {/* Playback Speed */}
                        <Select value={playbackSpeed.toString()} onValueChange={handleSpeedChange}>
                            <SelectTrigger className="h-7 w-16 bg-transparent border-none text-white text-xs hover:bg-secondary-foreground/20 focus:ring-0 focus:ring-offset-0 outline-none cursor-pointer">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent side="top" className="bg-popover border-border" container={containerRef.current}>
                                {PLAYBACK_SPEEDS.map((speed) => (
                                    <SelectItem key={speed} value={speed.toString()} className="text-xs">
                                        {`${speed}x`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Fullscreen */}
                        {allowFullscreen && (
                            <button
                                onClick={toggleFullscreen}
                                className="p-1.5 rounded hover:bg-secondary-foreground/20 text-white transition-colors outline-none cursor-pointer"
                                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                            >
                                {isFullscreen ? (
                                    <FiMinimize className="w-5 h-5" />
                                ) : (
                                    <FiMaximize className="w-5 h-5" />
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    )
}
